import { useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { CloseOutlined, FullscreenExitOutlined, FullscreenOutlined, MinusOutlined } from '@ant-design/icons';
import { Button, Flex, Typography } from 'antd';
import type { ResizeEdge } from './geometry';
import { applyDrag, applyResize } from './geometry';
import { useWindowGeometry } from './useWindowGeometry';
import { useWindowFocus } from './useWindowFocus';
import { nextZIndex, peekZIndex } from './zIndexCounter';
import { useHotkeyScope, useHotkeyActions, HotkeyHint, WindowScopeContext, WINDOW_SCOPE_ATTR, type HotkeyBinding } from '@/shared/lib/hotkeys';
import { HotkeyTip } from '@/shared/ui/hotkey';
import { useWindowHost } from '@/shared/lib/windows';

const RESIZE_EDGES: { edge: ResizeEdge; style: React.CSSProperties }[] = [
  { edge: 'n', style: { top: -3, left: 8, right: 8, height: 6, cursor: 'ns-resize' } },
  { edge: 's', style: { bottom: -3, left: 8, right: 8, height: 6, cursor: 'ns-resize' } },
  { edge: 'e', style: { right: -3, top: 8, bottom: 8, width: 6, cursor: 'ew-resize' } },
  { edge: 'w', style: { left: -3, top: 8, bottom: 8, width: 6, cursor: 'ew-resize' } },
  { edge: 'ne', style: { top: -3, right: -3, width: 10, height: 10, cursor: 'nesw-resize' } },
  { edge: 'nw', style: { top: -3, left: -3, width: 10, height: 10, cursor: 'nwse-resize' } },
  { edge: 'se', style: { bottom: -3, right: -3, width: 10, height: 10, cursor: 'nwse-resize' } },
  { edge: 'sw', style: { bottom: -3, left: -3, width: 10, height: 10, cursor: 'nesw-resize' } },
];

export interface FloatingWindowProps {
  /** Тип окна — по нему запоминаются размер/позиция (§1), не по конкретному документу. */
  kind: string;
  kindIndex?: number;
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  /** Доп. горячие клавиши конкретного окна (например, сохранение) — закрытие (Esc), сворачивание
   *  (Alt+M) и разворачивание (Alt+Enter) уже встроены и добавлять их не нужно. */
  hotkeys?: HotkeyBinding[];
  /** Куда ставить фокус при открытии: на `[data-autofocus]` или первое поле (`'auto'`) либо на
   *  само окно (`'window'`) — клавиши окна работают, ничего не раскрывается. */
  initialFocus?: 'auto' | 'window';
}

/**
 * Плавающее окно (§1: «модальные, свободно перемещаются и растягиваются по ширине и высоте,
 * размер и позиция запоминаются»). Без маски — можно открыть несколько окон одновременно
 * (продажа + возврат из неё, §11.1), страница под ними остаётся доступной.
 *
 * Escape закрывает окно, Alt+M сворачивает, Alt+Enter разворачивает на весь экран (1С-стиль
 * горячих клавиш, см. CLAUDE.md) — регистрируются здесь одним местом на все окна документов и
 * справочников; кнопки заголовка показывают те же клавиши в подсказке при наведении.
 * Несколько окон открыты одновременно без маски, и порядок их z-index меняется кликом
 * (`focus()` ниже) — приоритет горячих клавиш подтягивается следом через `touch()`, иначе Esc
 * всегда закрывал бы только последнее ОТКРЫТОЕ (а не активное) окно.
 *
 * Клавиатурный фокус окна — в `useWindowFocus`: при открытии на `[data-autofocus]` или первое
 * поле, при закрытии/сворачивании обратно туда, откуда окно открыли.
 *
 * Сворачивание (как в Windows): окно остаётся смонтированным и лишь скрывается через
 * `visibility: hidden` — так не теряются введённые данные, прокрутка, позиция и размер, а
 * RTK Query-подписки продолжают обновлять содержимое. Развёрнутое окно выходит на передний
 * план. Пока окно свёрнуто, его горячие клавиши (Esc) отключены.
 *
 * Максимизация (двойной клик по заголовку или кнопка) — чисто визуальная: окно растягивается на
 * весь viewport, но `rect` (позиция/размер, которые помнит `kind`) не трогается и в localStorage
 * не пишется, иначе разворачивание потом «забыло» бы прежний размер. Пока окно максимизировано,
 * перетаскивание и ручки ресайза отключены — как и в Windows, сначала нужно восстановить обычный
 * размер.
 */
export function FloatingWindow({ kind, kindIndex, title, onClose, footer, children, hotkeys, initialFocus }: FloatingWindowProps) {
  const { rect, setRect } = useWindowGeometry(kind, kindIndex);
  const [zIndex, setZIndex] = useState(() => nextZIndex());
  const host = useWindowHost();
  const minimized = host?.minimized ?? false;
  const setLabel = host?.setLabel;
  const label = typeof title === 'string' ? title : kind;

  // Развёрнутое окно — на передний план (подстройка состояния при смене пропса, не эффект).
  const [wasMinimized, setWasMinimized] = useState(minimized);
  if (wasMinimized !== minimized) {
    setWasMinimized(minimized);
    if (!minimized) setZIndex(nextZIndex());
  }

  useEffect(() => {
    setLabel?.(label);
  }, [setLabel, label]);

  const [maximized, setMaximized] = useState(false);
  const dragState = useRef<{ startX: number; startY: number; startRect: typeof rect } | null>(null);
  const resizeState = useRef<{ edge: ResizeEdge; startX: number; startY: number; startRect: typeof rect } | null>(null);

  // Клавиши окна — из каталога; в строке-подсказке внизу оставляем только закрытие (сворачивание
  // и разворачивание видны в подсказках кнопок заголовка).
  const chrome = useHotkeyActions({
    'window.close': { run: onClose },
    ...(host ? { 'window.minimize': { run: host.minimize, hint: false } } : {}),
    'window.maximize': { run: () => setMaximized((was) => !was), hint: false, label: maximized ? 'Восстановить' : undefined },
  });
  const bindings: HotkeyBinding[] = [...chrome.bindings, ...(hotkeys ?? [])];
  // `id` скоупа окна: если окно создал менеджер окон, он уже раздал его вложенным скоупам (тело
  // окна-компонента находится выше `FloatingWindow`, но ниже границы менеджера); иначе — свой.
  const ownScopeId = useId();
  const scopeId = useContext(WindowScopeContext) ?? ownScopeId;
  const { touch } = useHotkeyScope(bindings, !minimized, { level: 'window', id: scopeId });

  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const { restoreFocus, rememberFocus } = useWindowFocus({ rootRef, contentRef, footerRef, minimized, initialFocus });

  // Определён раньше остальных обработчиков (ниже) и эффектов-реакций на сигналы менеджера окон
  // (ещё ниже) — обе группы вызывают его из своих замыканий.
  const focus = () => {
    touch();
    host?.activate();
    setZIndex((z) => (z === peekZIndex() ? z : nextZIndex()));
  };

  // «Закрыть все» на панели свёрнутых окон: закрываем через обычный `onClose` (не минуя
  // проверку несохранённых изменений), только пока окно свёрнуто, и только один раз на
  // изменение сигнала — иначе эффект сработал бы повторно при каждом ре-рендере из-за нового
  // замыкания `onClose`.
  const closeAllSignal = host?.closeAllSignal;
  const seenCloseAllSignal = useRef(closeAllSignal);
  useEffect(() => {
    if (closeAllSignal === undefined || closeAllSignal === seenCloseAllSignal.current) return;
    seenCloseAllSignal.current = closeAllSignal;
    if (minimized) onClose();
  }, [closeAllSignal, minimized, onClose]);

  // Переключение между окнами по горячей клавише (Ctrl+M/Ctrl+Shift+M) — программный аналог
  // клика по заголовку: поднимает окно на передний план, его биндинги в приоритет, а клавиатурный
  // фокус — туда, где пользователь был в этом окне (иначе клавиши продолжали бы идти в поле
  // предыдущего окна, которое стоит ниже). `focus` —
  // новое замыкание на каждый рендер; срабатывание уже защищено сравнением с `seenFocusSignal`,
  // лишний перезапуск эффекта из-за identity `focus` ничего не делает повторно, поэтому в
  // зависимостях его не держим.
  const focusSignal = host?.focusSignal;
  const seenFocusSignal = useRef(focusSignal);
  useEffect(() => {
    if (focusSignal === undefined || focusSignal === seenFocusSignal.current) return;
    seenFocusSignal.current = focusSignal;
    focus();
    restoreFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- см. комментарий выше про `focus`.
  }, [focusSignal]);

  // Каскад/плитка — новая позиция/размер только для окон, попавших в эту раскладку (у остальных
  // `arrangeSignal` не меняется). Перекладка разом снимает максимизацию — пользователь явно
  // просит обычное окно в конкретном месте. Прямой вызов `setState` в эффекте здесь осознан: это
  // не производное состояние, а реакция на внешнюю команду менеджера окон (как и `onClose` выше).
  const arrangeSignal = host?.arrangeSignal;
  const arrangeRect = host?.arrangeRect;
  const seenArrangeSignal = useRef(arrangeSignal);
  useEffect(() => {
    if (arrangeSignal === undefined || arrangeSignal === seenArrangeSignal.current) return;
    seenArrangeSignal.current = arrangeSignal;
    if (arrangeRect) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- см. комментарий выше.
      setMaximized(false);
      setRect(arrangeRect);
    }
  }, [arrangeSignal, arrangeRect, setRect]);

  // `rect` сам по себе никогда не меняется, пока окно максимизировано (эффективный размер даёт
  // `effectiveRect` ниже) — снятие максимизации не требует отдельного «восстановить»: обычный
  // размер и так всё это время был цел.
  const toggleMaximize = () => {
    focus();
    setMaximized((was) => !was);
  };

  const onTitleDoubleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    toggleMaximize();
  };

  const effectiveRect = maximized ? { x: 0, y: 0, w: window.innerWidth, h: window.innerHeight } : rect;

  const onTitlePointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    focus();
    if (maximized) return; // можно поднять на передний план кликом, но не перетащить
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, startRect: rect };
  };

  const onTitlePointerMove = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setRect(applyDrag(dragState.current.startRect, dx, dy, { width: window.innerWidth, height: window.innerHeight }));
  };

  const endDrag = (e: React.PointerEvent) => {
    if (dragState.current) (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    dragState.current = null;
  };

  const onResizePointerDown = (edge: ResizeEdge) => (e: React.PointerEvent) => {
    focus();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    resizeState.current = { edge, startX: e.clientX, startY: e.clientY, startRect: rect };
  };

  const onResizePointerMove = (e: React.PointerEvent) => {
    if (!resizeState.current) return;
    const { edge, startX, startY, startRect } = resizeState.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    setRect(applyResize(startRect, edge, dx, dy, { width: window.innerWidth, height: window.innerHeight }));
  };

  const endResize = (e: React.PointerEvent) => {
    if (resizeState.current) (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    resizeState.current = null;
  };

  return (
    <div
      ref={rootRef}
      role="dialog"
      tabIndex={-1}
      {...{ [WINDOW_SCOPE_ATTR]: scopeId }}
      aria-hidden={minimized || undefined}
      onPointerDownCapture={focus}
      onFocusCapture={rememberFocus}
      style={{
        outline: 'none',
        position: 'fixed',
        left: effectiveRect.x,
        top: effectiveRect.y,
        width: effectiveRect.w,
        height: effectiveRect.h,
        zIndex,
        background: '#fff',
        border: '1px solid #8c8c8c',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        visibility: minimized ? 'hidden' : 'visible',
        pointerEvents: minimized ? 'none' : undefined,
        opacity: minimized ? 0 : 1,
        transition: 'opacity 120ms ease, visibility 120ms',
      }}
    >
      {maximized
        ? null
        : RESIZE_EDGES.map(({ edge, style }) => (
            <div
              key={edge}
              onPointerDown={onResizePointerDown(edge)}
              onPointerMove={onResizePointerMove}
              onPointerUp={endResize}
              style={{ position: 'absolute', ...style }}
            />
          ))}

      <Flex
        align="center"
        justify="space-between"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={endDrag}
        onDoubleClick={onTitleDoubleClick}
        style={{ padding: '4px 8px', background: '#f0f0f0', borderBottom: '1px solid #d9d9d9', cursor: maximized ? 'default' : 'move', flex: '0 0 auto' }}
      >
        <Typography.Text strong style={{ fontSize: 12 }}>
          {title}
        </Typography.Text>
        <Flex gap={2}>
          {host ? (
            <HotkeyTip action={chrome.get('window.minimize')} placement="bottom" display="inline-flex">
              <Button type="text" size="small" icon={<MinusOutlined />} aria-label="Свернуть" onClick={host.minimize} />
            </HotkeyTip>
          ) : null}
          <HotkeyTip action={chrome.get('window.maximize')} placement="bottom" display="inline-flex">
            <Button
              type="text"
              size="small"
              icon={maximized ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              aria-label={maximized ? 'Восстановить' : 'Развернуть на весь экран'}
              onClick={toggleMaximize}
            />
          </HotkeyTip>
          <HotkeyTip action={chrome.get('window.close')} placement="bottom" display="inline-flex">
            <Button type="text" size="small" icon={<CloseOutlined />} aria-label="Закрыть" onClick={onClose} />
          </HotkeyTip>
        </Flex>
      </Flex>

      <WindowScopeContext.Provider value={scopeId}>
        <div ref={contentRef} style={{ flex: '1 1 auto', overflow: 'auto', padding: 8 }}>
          {children}
        </div>

        {footer ? (
          <div ref={footerRef} style={{ flex: '0 0 auto', padding: 8, borderTop: '1px solid #d9d9d9' }}>
            {footer}
          </div>
        ) : null}
      </WindowScopeContext.Provider>
      <div style={{ flex: '0 0 auto', padding: '0 8px 6px' }}>
        <HotkeyHint bindings={bindings} />
      </div>
    </div>
  );
}
